(() => {
  const NATIVE_BUNDLE_HANDLES =
    window.GhaliaNativeBundleHandles ||
    new Set([
      'ghalia-infinity-ring-duo',
      'knot-bridge-duo-silver',
      'knot-bridge-duo-gold',
      'ghalia-knot-hero-set-gold',
      'ghalia-knot-hero-set-silver',
      'ghalia-knot-two-tone-ring-stack',
      'ghalia-knot-wrist-story',
      'ghalia-pave-gold-stack',
      'ghalia-pave-silver-stack',
      'ghalia-cross-ring-earring-gold',
    ]);

  const NO_SIZE_HANDLES = new Set(['knot-hoop-earring-gold', 'knot-hoop-earring-silver']);

  const bundleModeFor = (bundleHandle, components) => {
    if (bundleHandle === 'ghalia-cross-ring-earring-gold') return 'single';
    if (components.length > 2) return 'multi';
    return '2d';
  };

  const getBundleSpecs = () => {
    const componentsMap = window.GhaliaNativeBundleComponents || {};
    const discounts = window.GhaliaNativeBundleDiscounts || {};
    return Object.entries(componentsMap).map(([bundleHandle, components]) => ({
      bundleHandle,
      components,
      mode: bundleModeFor(bundleHandle, components),
      discountPct: Number(discounts[bundleHandle]) || 8,
    }));
  };

  const normalizeSize = (value) => String(value || '').replace(/\s+/g, ' ').trim();

  const looksLikeMaterial = (value) => {
    const v = normalizeSize(value).toLowerCase();
    if (!v) return true;
    if (/\(eu\)\s*\d{2}/i.test(v)) return false;
    if (/\(us\)/i.test(v)) return false;
    if (/\b(4[7-9]|5[0-9]|6[01])\b/.test(v)) return false;
    if (/\bxs\b|\bsmall\b|\bmedium\b|\blarge\b|\bxlarge\b/i.test(v)) return false;
    const materialWords = [
      'gold',
      'silver',
      'vermeil',
      'sterling',
      'two tone',
      'two-tone',
      'plated',
      '18k',
      '925',
      'rose',
      'white gold',
    ];
    return materialWords.some((word) => v.includes(word));
  };

  const euFromOptionString = (value) => {
    const s = String(value || '');
    const euTag = s.match(/\(EU\)\s*(\d{2})/i);
    if (euTag) return euTag[1];
    const nums = s.match(/\b(4[7-9]|5[0-9]|6[01])\b/g);
    if (nums?.length) return nums[nums.length - 1];
    return normalizeSize(s);
  };

  const sizeValuesMatch = (a, b) => {
    if (!a || !b) return false;
    if (normalizeSize(a) === normalizeSize(b)) return true;
    const euA = euFromOptionString(a);
    const euB = euFromOptionString(b);
    return Boolean(euA && euB && euA === euB);
  };

  const isFreeGiftLine = (item) => item?.properties?._ghalia_free_gift === 'true';

  const cartHasNativeBundle = (cart) =>
    (cart?.items || []).some((item) => NATIVE_BUNDLE_HANDLES.has(item?.handle));

  const extractSizeFromVariantTitle = (title) => {
    if (!title || title === 'Default Title') return '';
    const parts = title
      .split('/')
      .map((part) => normalizeSize(part))
      .filter(Boolean);
    for (const part of parts) {
      if (!looksLikeMaterial(part)) return part;
    }
    return '';
  };

  const extractSizeFromVariantRecord = (variant) => {
    if (!variant) return '';
    for (let i = 1; i <= 3; i += 1) {
      const val = variant[`option${i}`];
      if (val && !looksLikeMaterial(val)) return val;
    }
    return extractSizeFromVariantTitle(variant.title || '');
  };

  const extractSizeFromItemOptions = (item) => {
    const opts = item?.options_with_values || [];
    for (const o of opts) {
      const name = (o.name || '').toLowerCase();
      if (!name || name.includes('color') || name.includes('colour') || name.includes('material')) {
        continue;
      }
      if (name === 'earring' && !name.includes('size')) continue;
      if (name.includes('size') || name.includes('ring') || name.includes('bracelet')) {
        const value = o.value || '';
        if (value && !looksLikeMaterial(value)) return value;
      }
    }
    for (const o of opts) {
      const name = (o.name || '').toLowerCase();
      if (name.includes('color') || name.includes('colour') || name.includes('material')) continue;
      if (o.value && !looksLikeMaterial(o.value)) return o.value;
    }
    return '';
  };

  const lineSizeFromDom = (drawer, item) => {
    const key = item?.key;
    if (!key || !drawer) return '';
    const line = drawer.querySelector(`[data-cart-line-key="${CSS.escape(key)}"]`);
    const size = line?.dataset?.cartLineSize || '';
    return normalizeSize(size);
  };

  const variantCache = new Map();
  const bundleProductCache = new Map();

  const fetchVariant = async (variantId) => {
    const id = Number(variantId);
    if (!id) throw new Error('Missing variant');
    if (variantCache.has(id)) return variantCache.get(id);
    const root = window.Shopify?.routes?.root || '/';
    const res = await fetch(`${root}variants/${id}.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Variant unavailable');
    const data = await res.json();
    variantCache.set(id, data);
    return data;
  };

  const fetchBundleProduct = async (handle) => {
    if (bundleProductCache.has(handle)) return bundleProductCache.get(handle);
    const root = window.Shopify?.routes?.root || '/';
    const res = await fetch(`${root}products/${handle}.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Bundle unavailable');
    const data = await res.json();
    bundleProductCache.set(handle, data);
    return data;
  };

  const resolveSizeForItem = async (item, drawer) => {
    if (!item) return '';

    const domSize = lineSizeFromDom(drawer, item);
    if (domSize && !looksLikeMaterial(domSize)) return domSize;

    const fromOptions = extractSizeFromItemOptions(item);
    if (fromOptions) return fromOptions;

    const variantId = item.variant_id || item.id;
    if (variantId) {
      try {
        const variant = await fetchVariant(variantId);
        const fromVariant = extractSizeFromVariantRecord(variant);
        if (fromVariant) return fromVariant;
      } catch (err) {
        console.warn('[GhaliaCartBundleUpgrade] variant fetch failed', variantId, err);
      }
    }

    return extractSizeFromVariantTitle(item.variant_title || '');
  };

  const findBundleVariantId = (spec, picksByHandle) => {
    const product = spec._product;
    const variants = product?.variants || [];
    if (!variants.length) return null;

    if (spec.mode === 'multi') {
      return (
        variants.find((variant) =>
          spec.components.every((handle, idx) => {
            if (NO_SIZE_HANDLES.has(handle)) return true;
            const pick = picksByHandle[handle];
            const componentValue = variant[`option${idx + 1}`];
            return pick?.size && componentValue && sizeValuesMatch(pick.size, componentValue);
          })
        )?.id || null
      );
    }

    if (spec.mode === 'single') {
      const sizedHandle = spec.components.find((h) => !NO_SIZE_HANDLES.has(h));
      const pick = sizedHandle ? picksByHandle[sizedHandle] : null;
      if (!pick?.size) return null;
      return variants.find((variant) => sizeValuesMatch(variant.option1, pick.size))?.id || null;
    }

    const [first, second] = spec.components;
    const sizeA = picksByHandle[first]?.size;
    const sizeB = picksByHandle[second]?.size;
    if (!sizeA || !sizeB) return null;
    return (
      variants.find(
        (variant) =>
          sizeValuesMatch(variant.option1, sizeA) && sizeValuesMatch(variant.option2, sizeB)
      )?.id || null
    );
  };

  const groupComponentLines = (cart) => {
    const map = new Map();
    (cart?.items || []).forEach((item) => {
      if (isFreeGiftLine(item)) return;
      if (!item.handle || NATIVE_BUNDLE_HANDLES.has(item.handle)) return;
      if (!map.has(item.handle)) map.set(item.handle, []);
      map.get(item.handle).push(item);
    });
    return map;
  };

  const detectOpportunities = async (cart, drawer) => {
    const specs = getBundleSpecs();
    if (!specs.length || !cart?.items?.length || cartHasNativeBundle(cart)) return [];

    const byHandle = groupComponentLines(cart);
    const opportunities = [];

    for (const spec of specs) {
      const linePicks = {};
      let blocked = false;
      for (const handle of spec.components) {
        const lines = byHandle.get(handle) || [];
        if (!lines.length || lines.length > 1) {
          blocked = true;
          break;
        }
        linePicks[handle] = lines[0];
      }
      if (blocked) continue;

      const quantity = Math.min(
        ...spec.components.map((handle) => Number(linePicks[handle].quantity) || 0)
      );
      if (quantity <= 0) continue;

      const picksByHandle = {};
      for (const handle of spec.components) {
        const item = linePicks[handle];
        let size = '';
        if (!NO_SIZE_HANDLES.has(handle)) {
          size = await resolveSizeForItem(item, drawer);
          if (!size) {
            blocked = true;
            break;
          }
        }
        picksByHandle[handle] = { size, eu: euFromOptionString(size), item };
      }
      if (blocked) continue;

      let product;
      try {
        product = await fetchBundleProduct(spec.bundleHandle);
      } catch (err) {
        console.warn('[GhaliaCartBundleUpgrade] bundle fetch failed', spec.bundleHandle, err);
        continue;
      }
      const specWithProduct = { ...spec, _product: product };
      const bundleVariantId = findBundleVariantId(specWithProduct, picksByHandle);
      if (!bundleVariantId) continue;

      const updates = {};
      spec.components.forEach((handle) => {
        const item = linePicks[handle];
        updates[item.key] = Math.max(0, (Number(item.quantity) || 0) - quantity);
      });

      opportunities.push({
        spec,
        bundleVariantId: Number(bundleVariantId),
        quantity,
        updates,
        lineKeys: spec.components.map((handle) => linePicks[handle].key),
        picksByHandle,
        title: product.title || spec.bundleHandle,
        imageUrl: product.featured_image || product.images?.[0] || '',
      });
    }

    return opportunities;
  };

  const decodeCopy = (value) => {
    if (!value) return '';
    const el = document.createElement('textarea');
    el.innerHTML = value;
    return el.value;
  };

  const bundleThumbUrl = (url) => {
    if (!url) return '';
    try {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('width', '120');
      return parsed.toString();
    } catch {
      return url;
    }
  };

  const formatMessage = (opp, el) => {
    const template =
      decodeCopy(el?.dataset?.bundleUpgradeMessage) ||
      'Save {{ pct }}% on the {{ title }}.';
    return template
      .replace(/\{\{\s*pct\s*\}\}/gi, String(opp.spec.discountPct))
      .replace(/\{\{\s*title\s*\}\}/gi, opp.title);
  };

  const formatCta = (el) =>
    decodeCopy(el?.dataset?.bundleUpgradeCta) || 'Switch to set';

  const showError = (node, message) => {
    const err = node?.querySelector('[data-cart-bundle-upgrade-error]');
    if (!err) return;
    if (message) {
      err.textContent = message;
      err.hidden = false;
      err.removeAttribute('hidden');
    } else {
      err.textContent = '';
      err.hidden = true;
      err.setAttribute('hidden', '');
    }
  };

  const renderOpportunities = (drawer, opportunities) => {
    const container = drawer?.querySelector('[data-cart-bundle-upgrades]');
    const template = drawer?.querySelector('[data-cart-bundle-upgrade-template]');
    if (!container || !template) {
      if (!template) {
        console.warn('[GhaliaCartBundleUpgrade] upgrade template missing from cart drawer');
      }
      return;
    }

    container.innerHTML = '';
    if (!opportunities.length) {
      container.hidden = true;
      container.setAttribute('hidden', '');
      return;
    }

    container.hidden = false;
    container.removeAttribute('hidden');

    opportunities.forEach((opp) => {
      const node = template.content.firstElementChild.cloneNode(true);
      node.dataset.bundleHandle = opp.spec.bundleHandle;
      node.dataset.bundleVariantId = String(opp.bundleVariantId);
      const media = node.querySelector('[data-cart-bundle-upgrade-media]');
      const img = node.querySelector('[data-cart-bundle-upgrade-img]');
      const msg = node.querySelector('[data-cart-bundle-upgrade-msg]');
      const btn = node.querySelector('[data-cart-bundle-upgrade-btn]');
      if (img && opp.imageUrl) {
        img.src = bundleThumbUrl(opp.imageUrl);
        img.alt = opp.title || '';
        media?.removeAttribute('hidden');
      } else {
        media?.setAttribute('hidden', '');
      }
      if (msg) msg.textContent = formatMessage(opp, container);
      if (btn) {
        btn.textContent = formatCta(container);
        btn.addEventListener('click', async () => {
          if (btn.disabled) return;
          showError(node, '');
          btn.disabled = true;
          btn.setAttribute('aria-busy', 'true');
          try {
            if (!window.GhaliaCart?.upgradeToBundle) {
              throw new Error('Cart unavailable');
            }
            await window.GhaliaCart.upgradeToBundle(btn, {
              updates: opp.updates,
              variantId: opp.bundleVariantId,
              quantity: opp.quantity,
            });
          } catch (err) {
            console.error('[GhaliaCartBundleUpgrade]', err);
            const fallback =
              decodeCopy(container?.dataset?.bundleUpgradeError) ||
              'Could not switch to this set. Try again from the product page.';
            showError(node, err.message || fallback);
          } finally {
            btn.disabled = false;
            btn.removeAttribute('aria-busy');
          }
        });
      }
      container.appendChild(node);
    });
  };

  let syncToken = 0;

  const sync = async (cart, drawer) => {
    const rootDrawer = drawer || document.querySelector('[data-cart-drawer]');
    if (!rootDrawer) return;
    const token = ++syncToken;
    try {
      const opportunities = await detectOpportunities(cart, rootDrawer);
      if (token !== syncToken) return;
      renderOpportunities(rootDrawer, opportunities);
    } catch (err) {
      console.error('[GhaliaCartBundleUpgrade] sync failed', err);
    }
  };

  window.GhaliaCartBundleUpgrade = { sync, detectOpportunities };

  let bundleSyncTimer = null;

  const scheduleSync = (cart) => {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!cart?.items?.length || !drawer) return;
    clearTimeout(bundleSyncTimer);
    bundleSyncTimer = window.setTimeout(() => {
      sync(cart, drawer);
    }, 450);
  };

  document.addEventListener('ghalia:cart:update', (e) => scheduleSync(e.detail));
  document.addEventListener('ghalia:cart:pricing', (e) => scheduleSync(e.detail?.cart));
})();
